import { useEffect, useState, type ReactNode } from "react";
import { Simge, type SimgeAdi } from "./simgeler";
import { ApiHatasi } from "./api";

// --------------------------------------------------------------- veri cekme

export interface Cekim<T> {
  veri: T | null;
  yukleniyor: boolean;
  hata: ApiHatasi | null;
  yenile: () => void;
}

/** Basit veri cekme. Bagimlilik degisince yeniden cagirir. */
export function useCek<T>(f: () => Promise<T>, bagimliliklar: unknown[] = []): Cekim<T> {
  const [veri, setVeri] = useState<T | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<ApiHatasi | null>(null);
  const [sayac, setSayac] = useState(0);

  useEffect(() => {
    let iptal = false;
    setYukleniyor(true);
    setHata(null);
    f()
      .then((s) => {
        if (!iptal) setVeri(s);
      })
      .catch((e) => {
        if (!iptal) setHata(e instanceof ApiHatasi ? e : new ApiHatasi(String(e), "bilinmiyor", 0));
      })
      .finally(() => {
        if (!iptal) setYukleniyor(false);
      });
    return () => {
      iptal = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...bagimliliklar, sayac]);

  return { veri, yukleniyor, hata, yenile: () => setSayac((s) => s + 1) };
}

// --------------------------------------------------------------- parcalar

export function Kart({ baslik, eylem, children }: { baslik?: ReactNode; eylem?: ReactNode; children: ReactNode }) {
  return (
    <section className="kart">
      {baslik && (
        <h2>
          <span>{baslik}</span>
          {eylem}
        </h2>
      )}
      {children}
    </section>
  );
}

export function Olcu({
  ad,
  deger,
  birim,
  not,
  oran,
}: {
  ad: string;
  deger: ReactNode;
  birim?: string;
  not?: ReactNode;
  oran?: number | null;
}) {
  const sinif = oran == null ? "" : oran >= 1 ? " dolu" : oran >= 0.8 ? " orta" : "";
  return (
    <div className="kart olcu">
      <div className="ad">{ad}</div>
      <div className="deger">
        {deger}
        {birim && <small>{birim}</small>}
      </div>
      {oran != null && (
        <div className={"cubuk" + sinif}>
          <i style={{ width: Math.min(100, Math.max(2, oran * 100)) + "%" }} />
        </div>
      )}
      {not && <div className="not">{not}</div>}
    </div>
  );
}

export function Rozet({ tur = "", children }: { tur?: "iyi" | "uyari" | "kotu" | "marka" | ""; children: ReactNode }) {
  return <span className={"rozet " + tur}>{children}</span>;
}

/** Kutu/kiraci durumunu tek yerden renklendir — ekranlar arasinda tutarli kalsin */
export function DurumRozeti({ durum }: { durum: string }) {
  if (durum === "aktif") return <Rozet tur="iyi">aktif</Rozet>;
  if (durum === "askida") return <Rozet tur="kotu">askida</Rozet>;
  if (durum === "gonderim_kilitli") return <Rozet tur="uyari">gönderim kilitli</Rozet>;
  if (durum === "silinmeyi_bekliyor") return <Rozet tur="uyari">silinmeyi bekliyor</Rozet>;
  return <Rozet>{durum}</Rozet>;
}

const BILDIRIM_SIMGE: Record<string, SimgeAdi> = {
  iyi: "ok",
  uyari: "uyari",
  kotu: "carpi",
  "": "bilgi",
};

export function Bildirim({ tur = "", children }: { tur?: "iyi" | "uyari" | "kotu" | ""; children: ReactNode }) {
  // Simge, rengi zaten tasiyan kutuya IKINCI bir isaret ekler: renk korlugu
  // olan kullanici icin uyari ile bilgi arasindaki fark yalnizca renk olmamali.
  return (
    <div className={"bildirim " + tur}>
      <Simge ad={BILDIRIM_SIMGE[tur] ?? "bilgi"} boyut={17} />
      <div>{children}</div>
    </div>
  );
}


export function HataKutusu({ hata }: { hata: ApiHatasi }) {
  return (
    <Bildirim tur="kotu">
      {hata.message}
      {hata.istekId && <span className="istekId">Destek için istek kimliği: {hata.istekId}</span>}
    </Bildirim>
  );
}

export function Durum<T>({
  cekim,
  bos,
  children,
}: {
  cekim: Cekim<T>;
  bos?: string;
  children: (veri: T) => ReactNode;
}) {
  if (cekim.yukleniyor) return <div className="yukleniyor">Yükleniyor…</div>;
  if (cekim.hata) return <HataKutusu hata={cekim.hata} />;
  if (!cekim.veri) return <div className="bos">{bos ?? "Kayıt yok"}</div>;
  return <>{children(cekim.veri)}</>;
}

export function Tablo({ basliklar, children }: { basliklar: ReactNode[]; children: ReactNode }) {
  return (
    <div className="sarma">
      <table className="tablo">
        <thead>
          <tr>
            {basliklar.map((b, i) => (
              <th key={i} className={typeof b === "string" && b.startsWith("#") ? "sayi" : ""}>
                {typeof b === "string" && b.startsWith("#") ? b.slice(1) : b}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Bos({ children }: { children: ReactNode }) {
  return <div className="bos">{children}</div>;
}

/** Tek kullanimlik sir — parola gibi bir daha gosterilmeyecek degerler icin */
export function TekSeferlik({ baslik, deger, kapat }: { baslik: string; deger: string; kapat: () => void }) {
  const [kopyalandi, setKopyalandi] = useState(false);
  return (
    <Bildirim tur="uyari">
      <strong>{baslik}</strong>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
        <code className="sir">{deger}</code>
        <button
          className="dugme kucuk"
          onClick={() => {
            navigator.clipboard?.writeText(deger).then(
              () => setKopyalandi(true),
              () => setKopyalandi(false),
            );
          }}
        >
          {kopyalandi ? "kopyalandı" : "kopyala"}
        </button>
        <button className="dugme kucuk" onClick={kapat}>
          kapat
        </button>
      </div>
      <small style={{ display: "block", marginTop: 8 }}>
        Bu değer bir daha gösterilmeyecek. Şimdi kaydedin ya da ilgili kişiye iletin.
      </small>
    </Bildirim>
  );
}
